import {
  Checkbox,
  checkboxStates,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import Field, { fieldTypes } from "components/Field";
import { modalTypes } from "constants/types";
import classes from "layout/systemAdmin/users/modals/NewUserModal.module.scss";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { selectIsGovMode } from "slices/app/selectors";
import { editUser } from "slices/systemAdmin/actions/users";
import { selectIsUsernameAndPassword } from "slices/systemAdmin/selectors";

const EditUserModal = ({
  currentUserName,
  currentFirstName,
  currentLastName,
  currentSystemAdmin,
  onlyOneSystemAdmin,
}) => {
  const dispatch = useDispatch();

  const [firstName, setFirstName] = useState(currentFirstName);
  const [lastName, setLastName] = useState(currentLastName);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [systemAdmin, setSystemAdmin] = useState(currentSystemAdmin);

  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const isUserNamePassword = useSelector(selectIsUsernameAndPassword);
  const isGovMode = useSelector(selectIsGovMode);
  const showPasswordField = !isGovMode && (isUserNamePassword || systemAdmin);

  const isEnabled = () =>
    firstNameError === false &&
    lastNameError === false &&
    passwordError === "" &&
    confirmPasswordError === "" &&
    password === confirmPassword;

  const handleEditUser = () => {
    dispatch(
      editUser(
        currentUserName.trim(),
        firstName.trim(),
        lastName.trim(),
        password.trim(),
        systemAdmin
      )
    );
  };

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.EDIT_USER));
  };

  const onEnterKeyDown = ({ key }) => {
    if (key === "Enter" && isEnabled()) {
      handleEditUser();
    }
  };

  const userNameBody = () => (
    <label
      htmlFor="edit-user-modal-user-name"
      className="edit__user__modal__username"
    >
      <b>User Name</b>
      <input
        type="text"
        id="edit-user-modal-user-name"
        value={currentUserName}
        disabled
        data-clarity-mask="True"
      />
    </label>
  );

  const adminCheckbox = () => (
    <div
      className="edit__user__modal__admin"
      title={
        onlyOneSystemAdmin &&
        currentSystemAdmin &&
        "Cannot remove the only system admin user. Please add another system admin before continuing."
      }
    >
      <span>
        <b>System Administrator</b>
      </span>
      <Checkbox
        state={systemAdmin ? checkboxStates.checked : checkboxStates.empty}
        handleChange={() => {
          setSystemAdmin(!systemAdmin);
        }}
        disabled={onlyOneSystemAdmin && currentSystemAdmin}
        id="cb-edit-user-system-admin"
        value="edit-user-system-admin-checkbox"
      />
    </div>
  );

  return (
    <Modal
      data-test="editUserModal"
      className={clsx("fade-in", classes.modal_container)}
      show
      onDismiss={handleCancel}
      backdrop
      centered
      onKeyDown={onEnterKeyDown}
    >
      <Modal.Header>
        <b className="edit__user__modal__header">Edit User</b>
      </Modal.Header>
      <Modal.Body className={classes.modal_body}>
        <div className="edit__user__modal">
          {userNameBody()}
          <Field
            type={fieldTypes.firstName}
            propsObject={{
              firstName,
              setFirstName,
              firstNameError,
              setFirstNameError,
            }}
          />

          <Field
            type={fieldTypes.lastName}
            propsObject={{
              lastName,
              setLastName,
              lastNameError,
              setLastNameError,
            }}
          />

          {showPasswordField && (
            <Field
              type={fieldTypes.passwordAndConfirm}
              propsObject={{
                password,
                setPassword,
                passwordError,
                setPasswordError,
                confirmPassword,
                setConfirmPassword,
                confirmPasswordError,
                setConfirmPasswordError,
              }}
            />
          )}
          {adminCheckbox()}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancelButton"
          variant={NewButtonVariant.l2}
          onClick={handleCancel}
        >
          Cancel
        </NewButton>

        <NewButton
          data-testid="saveButton"
          variant={NewButtonVariant.l1}
          onClick={handleEditUser}
          disabled={!isEnabled()}
        >
          Save
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default EditUserModal;

EditUserModal.propTypes = {
  currentUserName: PropTypes.string.isRequired,
  currentFirstName: PropTypes.string.isRequired,
  currentLastName: PropTypes.string.isRequired,
  currentSystemAdmin: PropTypes.bool.isRequired,
  onlyOneSystemAdmin: PropTypes.bool.isRequired,
};
